import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  deleteDoc,
  where,
  type Timestamp,
} from 'firebase/firestore'
import type {
  CollectionReference,
  DocumentReference,
  WithFieldValue,
  UpdateData,
} from 'firebase/firestore'
import { db } from '@/firebaseConfig'
import type { ActionTree, GetterTree, MutationTree, Module } from 'vuex'

export type AttendanceStatus = 'attending' | 'not_attending' | 'maybe'

interface EventMembershipBase {
  userId: string
  name: string
  thumbnail: string
  status: AttendanceStatus
  teamIds: string[]
  preferredTeamIds?: string[]
  skills?: {id: string, title: string}
}
export interface EventMembershipDoc extends EventMembershipBase {
  createdAt: Timestamp
  updatedAt: Timestamp
}

/**
 * One of today's events the user is on a team of, for the home screen's
 * greeting: the event and the titles of the teams they serve on.
 */
export interface TodaysAssignment {
  eventId: string
  title: string
  start: Date
  teams: { id: string; title: string }[]
}

export interface MembershipsState {
  byEvent: Record<string, Record<string, EventMembershipDoc>>
  lastLoadedAt: Record<string, number>
  fetching: boolean
  error: string | null
  todaysAssignments: TodaysAssignment[]
}

const state = (): MembershipsState => ({
  byEvent: {},
  lastLoadedAt: {},
  fetching: false,
  error: null,
  todaysAssignments: [],
})

const mutations: MutationTree<MembershipsState> = {
  SET_FETCHING(state, val: boolean) {
    state.fetching = val
  },
  SET_ERROR(state, msg: string | null) {
    state.error = msg
  },
  SET_EVENT_MEMBERSHIPS(
    state,
    { eventId, memberships }: { eventId: string; memberships: Record<string, EventMembershipDoc> }
  ) {
    state.byEvent[eventId] = memberships
  },
  SET_EVENT_FETCHED_AT(state, { eventId, at }: { eventId: string; at: number }) {
    state.lastLoadedAt[eventId] = at
  },
  UPSERT_MEMBERSHIP(
    state,
    { eventId, membership }: { eventId: string; membership: EventMembershipDoc }
  ) {
    if (!state.byEvent[eventId]) state.byEvent[eventId] = {}
    state.byEvent[eventId][membership.userId] = membership
  },
  REMOVE_MEMBERSHIP(state, { eventId, userId }: { eventId: string; userId: string }) {
    if (!state.byEvent[eventId]) return
    delete state.byEvent[eventId][userId]
  },
  SET_TODAYS_ASSIGNMENTS(state, assignments: TodaysAssignment[]) {
    state.todaysAssignments = assignments
  },
}

const getters: GetterTree<MembershipsState, any> = {
  // Do we have any data cached for this event?
  hasEvent:
    (state) =>
    (eventId: string): boolean =>
      !!state.byEvent[eventId] && Object.keys(state.byEvent[eventId]).length > 0,

  // Is the cached data older than TTL? (default 60s)
  isEventStale:
    (state) =>
    (eventId: string, ttlMs = 60_000): boolean => {
      const ts = state.lastLoadedAt[eventId] ?? 0
      return Date.now() - ts > ttlMs
    },

  // Maps & arrays
  eventMembershipsMap:
    (state) =>
    (eventId: string): Record<string, EventMembershipDoc> =>
      state.byEvent[eventId] ?? {},

  eventMemberships:
    (_state, g) =>
    (eventId: string): EventMembershipDoc[] =>
      Object.values(g.eventMembershipsMap(eventId)),

  // Single membership (for “me” or any user)
  userMembership:
    (state) =>
    (eventId: string, userId: string): EventMembershipDoc | null =>
      state.byEvent[eventId]?.[userId] ?? null,

  // Rosters & counts
  teamRoster:
    (_state, g) =>
    (eventId: string, teamId: string): EventMembershipDoc[] =>
      g.eventMemberships(eventId).filter((m: EventMembershipDoc) => m.teamIds.includes(teamId)),

  attendingRoster:
    (_state, g) =>
    (eventId: string): EventMembershipDoc[] =>
      g.eventMemberships(eventId).filter((m: EventMembershipDoc) => m.status === 'attending'),

  notAttendingRoster:
    (_state, g) =>
    (eventId: string): EventMembershipDoc[] =>
      g.eventMemberships(eventId).filter((m: EventMembershipDoc) => m.status === 'not_attending'),

  teamCount:
    (_s, g) =>
    (eventId: string, teamId: string): number =>
      g.teamRoster(eventId, teamId).length,

  attendingCount: (_s, g) => (eventId: string, status: string): number =>
    status == "attending" ? g.attendingRoster(eventId).length : g.notAttendingRoster(eventId).length,

  todaysAssignments: (state): TodaysAssignment[] => state.todaysAssignments,
}

const actions: ActionTree<MembershipsState, any> = {
  /**
   * The teams the user serves on at today's events, as `/api/assignments`
   * finds them for the apps: the organisation's events starting today, then
   * the user's own membership document on each. Events where they are not
   * attending, or attending without a team, are left out.
   */
  async fetchTodaysAssignments(
    { commit },
    { accountId, userId }: { accountId: number | null; userId: string | null }
  ) {
    if (accountId == null || !userId) {
      commit('SET_TODAYS_ASSIGNMENTS', [])
      return
    }
    try {
      const now = new Date()
      const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
      const snap = await getDocs(
        query(
          collection(db, 'events'),
          where('account', '==', accountId),
          where('start', '>=', dayStart),
          where('start', '<', dayEnd),
          orderBy('start', 'asc')
        )
      )

      const assignments = await Promise.all(
        snap.docs.map(async (eventDoc): Promise<TodaysAssignment | null> => {
          const membershipSnap = await getDoc(
            doc(db, `events/${eventDoc.id}/memberships/${userId}`)
          )
          if (!membershipSnap.exists()) return null
          const membership = membershipSnap.data() as Partial<EventMembershipDoc>
          const teamIds = Array.isArray(membership.teamIds) ? membership.teamIds : []
          if (membership.status === 'not_attending' || teamIds.length === 0) return null

          const teamsSnap = await getDocs(collection(db, `events/${eventDoc.id}/teams`))
          const titles = new Map(
            teamsSnap.docs.map((teamDoc) => [teamDoc.id, String(teamDoc.data().title ?? '').trim()])
          )
          const teams = teamIds
            .map((id) => ({ id, title: titles.get(id) ?? '' }))
            .filter((team) => team.title)
          if (teams.length === 0) return null

          const data = eventDoc.data()
          const start = data.start?.toDate ? data.start.toDate() : new Date(data.start)
          return { eventId: eventDoc.id, title: String(data.title ?? ''), start, teams }
        })
      )
      commit(
        'SET_TODAYS_ASSIGNMENTS',
        assignments.filter((a): a is TodaysAssignment => a !== null)
      )
    } catch (e: any) {
      commit('SET_ERROR', e?.message ?? String(e))
      commit('SET_TODAYS_ASSIGNMENTS', [])
    }
  },

  // Only fetch if not loaded or stale (use in router/nav guards)
  async ensure({ getters, dispatch }, { eventId, ttlMs = 60_000 }: { eventId: string; ttlMs?: number }) {
    if (!getters.hasEvent(eventId) || getters.isEventStale(eventId, ttlMs)) {
      await dispatch('fetchAll', { eventId })
    }
  },

  // Load all memberships for an event
  async fetchAll({ commit }, { eventId }: { eventId: string }) {
    commit('SET_FETCHING', true)
    commit('SET_ERROR', null)
    try {
      const coll = collection(db, `events/${eventId}/memberships`) as CollectionReference<EventMembershipDoc>
      const snap = await getDocs(coll)

      const map: Record<string, EventMembershipDoc> = {}
      snap.forEach((d) => {
        const data = d.data()
        map[data.userId] = data
      })
      commit('SET_EVENT_MEMBERSHIPS', { eventId, memberships: map })
      commit('SET_EVENT_FETCHED_AT', { eventId, at: Date.now() })
    } catch (e: any) {
      commit('SET_ERROR', e?.message ?? String(e))
    } finally {
      commit('SET_FETCHING', false)
    }
  },

  // Ensure user is attending and add them to a team
  async joinTeam(
    { commit },
    { eventId, userId, name, thumbnail, teamId }: { eventId: string; userId: string; name: string; thumbnail: string; teamId: string }
  ) {
    const coll = collection(db, `events/${eventId}/memberships`) as CollectionReference<EventMembershipDoc>
    const ref = doc(coll, userId) as DocumentReference<EventMembershipDoc>

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      const now = serverTimestamp()

      if (!snap.exists()) {
        const payload = {
          userId,
          name,
          thumbnail,
          status: 'attending' as const,
          teamIds: [teamId],
          createdAt: now,
          updatedAt: now,
        } satisfies WithFieldValue<EventMembershipDoc>
        tx.set(ref, payload)
        return
      }

      const cur = snap.data()
      const set = new Set(cur.teamIds)
      set.add(teamId)

      const updates: UpdateData<EventMembershipDoc> = {
        status: 'attending',
        teamIds: Array.from(set),
        updatedAt: now,
      }
      tx.update(ref, updates)
    })

    const fresh = await getDoc(ref)
    if (fresh.exists()) {
      commit('UPSERT_MEMBERSHIP', { eventId, membership: fresh.data() })
    }
  },

  // Remove user from a team
  async leaveTeam(
    { commit },
    { eventId, userId, teamId }: { eventId: string; userId: string; teamId: string }
  ) {
    const coll = collection(db, `events/${eventId}/memberships`) as CollectionReference<EventMembershipDoc>
    const ref = doc(coll, userId) as DocumentReference<EventMembershipDoc>

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      if (!snap.exists()) return
      const cur = snap.data()
      const next = cur.teamIds.filter((t) => t !== teamId)

      const updates: UpdateData<EventMembershipDoc> = {
        teamIds: next,
        updatedAt: serverTimestamp(),
      }
      tx.update(ref, updates)
    })

    const fresh = await getDoc(ref)
    if (fresh.exists()) {
      commit('UPSERT_MEMBERSHIP', { eventId, membership: fresh.data() })
    }
  },

  // Update attending / not_attending / maybe
  async setAttendance(
    { commit },
    {
      eventId,
      userId,
      name,
      thumbnail,
      preferredTeams,
      skills,
      status,
      clearTeamsWhenNotAttending = true,
    }: { eventId: string; userId: string; name: string; thumbnail: string; preferredTeams: string[]; skills: {id: string, title: string}; status: AttendanceStatus; clearTeamsWhenNotAttending?: boolean }
  ) {
    const coll = collection(db, `events/${eventId}/memberships`) as CollectionReference<EventMembershipDoc>
    const ref = doc(coll, userId) as DocumentReference<EventMembershipDoc>

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref)
      const now = serverTimestamp()

      if (!snap.exists()) {
        const payload = {
          userId,
          name,
          thumbnail,
          status,
          teamIds: [],
          preferredTeamIds: preferredTeams,
          skills: skills ?? null,
          createdAt: now,
          updatedAt: now,
        } satisfies WithFieldValue<EventMembershipDoc>
        tx.set(ref, payload)
        return
      }

      const updates: UpdateData<EventMembershipDoc> = { status, updatedAt: now }
      if (status === 'not_attending' && clearTeamsWhenNotAttending) {
        updates.teamIds = []
      }
      tx.update(ref, updates)
    })

    const fresh = await getDoc(ref)
    if (fresh.exists()) {
      commit('UPSERT_MEMBERSHIP', { eventId, membership: fresh.data() })
    }
  },
  async removeMembership(
    { commit },
    { eventId, userId }: { eventId: string; userId: string }
  ) {
    const coll = collection(db, `events/${eventId}/memberships`) as CollectionReference<EventMembershipDoc>
    const ref = doc(coll, userId) as DocumentReference<EventMembershipDoc>

    // Idempotent delete (no-ops if it doesn't exist)
    await deleteDoc(ref)

    // Update local cache
    commit('REMOVE_MEMBERSHIP', { eventId, userId })
  },
}

const memberships: Module<MembershipsState, any> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}

export default memberships